import React from "react";
import { Typography, ModalProps } from "antd";

import {
  Modal,
  Header,
  DoneContainer,
  DoneFrame,
  DoneText,
  Small,
} from "./styles/successModal";

import { scrim } from "src/theme/ui";
import { Button, DialogActions, DialogMark } from "src/components/ui";

interface SuccessModalProps extends ModalProps {
  icon?: string;
  text?: React.ReactNode;
  title?: React.ReactNode;
  definition?: React.ReactNode;
  smallText?: React.ReactNode;
  onDone?: () => void;
}

const SuccessModal = ({
  visible,
  onOk,
  onCancel,
  icon,
  title,
  text,
  definition,
  smallText,
  onDone,
  ...rest
}: SuccessModalProps) => {
  /**
   * This used to close itself after ten seconds.
   *
   * The handle was never captured — `let timeId` was declared and the
   * setTimeout result thrown away — so the cleanup cleared nothing. A timer
   * from one confirmation therefore survived it and fired later, closing
   * whatever success modal happened to be open at the time.
   *
   * The timer is gone rather than repaired. This is the screen that tells
   * somebody their deposit request was placed, or their password changed; it
   * should still be there when they look up from their phone. It closes when
   * they say so.
   */

  return (
    <Modal
      wrapClassName="fina-dialog"
      maskStyle={{ background: scrim, backdropFilter: "none" }}
      title={<Header>{title}</Header>}
      visible={visible}
      onOk={onOk}
      /* The corner X did nothing on most of these. It was wired to
         `onCancel`, and the callers that matter — the end of the deposit
         flow, the end of an order — pass `onDone` and no `onCancel`, so the
         only control in the dialog's corner was dead. It falls back to the
         same thing the button does. */
      onCancel={onCancel ?? onDone}
      footer={[
        /* Was a 40%-wide box centring an 80%-wide legacy button with a
           circled-arrow glyph beside the word. The action row every other
           dialog ends with, and the button the product actually uses. */
        <DialogActions key="customFooter">
          <Button
            variant="primary"
            size="lg"
            onClick={onDone}
            className="test-success-button"
          >
            Done
          </Button>
        </DialogActions>,
      ]}
      {...rest}
    >
      <DoneContainer>
        {/* Was the caller's illustration, drawn at its natural size. A
              confirmation wears the same mark as every other dialog that
              reports an outcome; the `icon` prop is left in the signature so
              the callers do not all have to change at once. */}
          <DialogMark tone="success" />
        <Typography.Text>
          <DoneFrame>
            <DoneText>{text}</DoneText>
          </DoneFrame>
        </Typography.Text>
        <Small>{smallText}</Small>
        <DoneFrame width="70%">
          <Typography.Text>{definition}</Typography.Text>
        </DoneFrame>
      </DoneContainer>
    </Modal>
  );
};

export default SuccessModal;
